/**
 * Client-side media validation utilities for aspect ratio and dimension checks.
 */

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.svg', '.avif'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.mkv', '.avi'];

/**
 * Detect if a file is an image based on MIME type or extension.
 */
export function isImageFile(file) {
  if (file.type?.startsWith('image/')) return true;
  const ext = '.' + (file.name || '').split('.').pop().toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
}

/**
 * Detect if a file is a video based on MIME type or extension.
 */
export function isVideoFile(file) {
  if (file.type?.startsWith('video/')) return true;
  const ext = '.' + (file.name || '').split('.').pop().toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext);
}

/**
 * Extract dimensions (width, height) from a File object — plus `duration`
 * (whole seconds) for videos. Duration is critical for scheduling: without it
 * the backend falls back to a 15s still-image default, so two videos looped
 * together each get cut to 15s regardless of their real length.
 * @param {File} file
 * @returns {Promise<{width: number, height: number, duration?: number}>}
 */
export function getMediaDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);

    if (isImageFile(file)) {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image dimensions'));
      };
      img.src = url;
    } else if (isVideoFile(file)) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const duration = Number.isFinite(video.duration) && video.duration > 0
          ? Math.round(video.duration)
          : undefined;
        resolve({ width: video.videoWidth, height: video.videoHeight, duration });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load video dimensions'));
      };
      video.src = url;
    } else {
      URL.revokeObjectURL(url);
      reject(new Error('Unsupported file type for dimension extraction'));
    }
  });
}

function gcd(a, b) {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) { [a, b] = [b, a % b]; }
  return a;
}

/**
 * Compute simplified aspect ratio string.
 * @param {number} w
 * @param {number} h
 * @returns {string} e.g. "16:9", "9:16"
 */
export function computeAspectRatio(w, h) {
  if (!w || !h) return null;
  const d = gcd(w, h);
  return `${w / d}:${h / d}`;
}

/**
 * Get orientation from dimensions.
 */
export function getOrientation(w, h) {
  if (!w || !h) return null;
  if (w > h) return 'landscape';
  if (h > w) return 'portrait';
  return 'square';
}

/**
 * Check if uploaded content dimensions match the target screen's aspect ratio.
 * Uses 2% tolerance for rounding differences.
 * @param {number} fileW - uploaded content width
 * @param {number} fileH - uploaded content height
 * @param {number} screenW - target screen native width
 * @param {number} screenH - target screen native height
 * @returns {{ valid: boolean, expected: string, actual: string, expectedOrientation: string, actualOrientation: string }}
 */
export function checkAspectRatioMatch(fileW, fileH, screenW, screenH) {
  const expected = computeAspectRatio(screenW, screenH);
  const actual = computeAspectRatio(fileW, fileH);
  const expectedOrientation = getOrientation(screenW, screenH);
  const actualOrientation = getOrientation(fileW, fileH);

  const ratio1 = fileW / fileH;
  const ratio2 = screenW / screenH;
  const valid = Math.abs(ratio1 - ratio2) / Math.max(ratio1, ratio2) < 0.02;

  return { valid, expected, actual, expectedOrientation, actualOrientation };
}

/**
 * Generate a JPEG data URL poster from a video file by drawing the first frame
 * (or a slightly-offset frame to avoid black intros) to an offscreen canvas.
 *
 * @param {File} file
 * @param {{ maxDim?: number, timeoutMs?: number }} [opts]
 * @returns {Promise<string>} data URL
 */
export function generateVideoPoster(file, opts = {}) {
  const { maxDim = 320, timeoutMs = 5000 } = opts;
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    let settled = false;
    const cleanup = () => {
      settled = true;
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      video.src = '';
      video.removeAttribute('src');
      try { video.load(); } catch { /* noop */ }
    };
    const fail = (err) => {
      if (settled) return;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err || 'Video poster failed')));
    };
    const done = (dataUrl) => {
      if (settled) return;
      cleanup();
      resolve(dataUrl);
    };

    const timer = setTimeout(() => fail(new Error('Video poster timeout')), timeoutMs);

    video.onloadedmetadata = () => {
      const dur = Number.isFinite(video.duration) ? video.duration : 0;
      const seek = dur > 0 ? Math.min(1, dur * 0.1) : 0.1;
      try {
        video.currentTime = seek;
      } catch (e) {
        clearTimeout(timer);
        fail(e);
      }
    };
    video.onseeked = () => {
      try {
        const vw = video.videoWidth || 0;
        const vh = video.videoHeight || 0;
        if (!vw || !vh) throw new Error('Video has no intrinsic size');
        const scale = Math.min(1, maxDim / Math.max(vw, vh));
        const cw = Math.max(1, Math.round(vw * scale));
        const ch = Math.max(1, Math.round(vh * scale));
        const canvas = document.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, cw, ch);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        clearTimeout(timer);
        done(dataUrl);
      } catch (e) {
        clearTimeout(timer);
        fail(e);
      }
    };
    video.onerror = () => {
      clearTimeout(timer);
      fail(new Error('Video element error while generating poster'));
    };

    video.src = url;
  });
}

/**
 * Generate a thumbnail descriptor for a file. Returns an object with:
 *   - url: blob URL (images) or data URL (video poster)
 *   - type: 'image' or 'video-poster'
 *   - revocable: true if the caller should URL.revokeObjectURL(url) on cleanup
 * Throws if the file is neither image nor video.
 */
export async function generateThumbnail(file) {
  if (isImageFile(file)) {
    return { url: URL.createObjectURL(file), type: 'image', revocable: true };
  }
  if (isVideoFile(file)) {
    const url = await generateVideoPoster(file);
    return { url, type: 'video-poster', revocable: false };
  }
  throw new Error('Unsupported file type for thumbnail');
}
